/*
  Pre-order expected date line item property for add-to-cart requests the theme does not render.

  Theme forms (product page, product cards, featured product) already carry the property as a
  hidden input from snippets/preorder-expected-date.liquid. Fast Simon's collection and search
  grids and UpCart's drawer post to /cart/add themselves, so this wraps fetch and XHR, looks up
  the variant's property, and adds it before the request is sent. Any failure sends the original
  request unchanged.

  The add waits on the lookup, so the answer should already be known by the time of the tap:
  - Theme forms print each product's lookup data next to the hidden input; it is read from the
    page first.
  - Fast Simon grid cards are looked up in batches of 20 through templates/search.preorder-data
    as soon as they are drawn, including after infinite scroll and filter changes.
  - Anything else falls back to sections/preorder-expected-date-data.liquid, one product at a time.
*/
(function () {
  if (window.preorderExpectedDate) return;

  const SECTION_ID = 'preorder-expected-date-data';
  const BATCH_VIEW = 'preorder-data';
  // all_products resolves at most 20 handles per page render.
  const BATCH_SIZE = 20;
  const LOOKUP_TIMEOUT = 2500;
  const CART_ADD_PATTERN = /\/cart\/add(\.js|\.json)?(\?|$)/;
  const PRODUCT_PATH_PATTERN = /\/products\/([^/?#]+)/;
  const PAGE_DATA_SELECTOR = 'script[data-preorder-expected-date-data]';
  const FAST_SIMON_CARD_SELECTOR = '.fs-results-product-card';

  // variant id -> Promise<{ name, value } | null>
  const variantLookups = new Map();
  // product handle -> Promise<data | null>
  const productLookups = new Map();
  // Page data blocks and Fast Simon cards already handled.
  const seenElements = new WeakSet();
  // Batch requests still in flight, and handles waiting for the next one.
  const pendingBatches = new Set();
  const queuedHandles = new Set();
  let flushTimer = null;

  // Read lazily: this file loads early in <head>, before Shopify.routes is defined.
  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function withTimeout(promise) {
    return Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), LOOKUP_TIMEOUT))]);
  }

  // Stores a product's lookup data (from the page, a batch or the data section) per variant.
  function recordProductData(data) {
    if (!data || !Array.isArray(data.variants)) return null;
    data.variants.forEach((id) => {
      const isPreorder = data.property && data.preorderVariants.includes(id);
      variantLookups.set(String(id), Promise.resolve(isPreorder ? data.property : null));
    });
    return data;
  }

  function seedFromPage() {
    document.querySelectorAll(PAGE_DATA_SELECTOR).forEach((script) => {
      if (seenElements.has(script)) return;
      seenElements.add(script);
      try {
        recordProductData(JSON.parse(script.textContent));
      } catch (error) {
        // A malformed block only costs this product its shortcut.
      }
    });
  }

  function fetchSectionData(handle) {
    const url = `${rootUrl()}products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`;
    return originalFetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector(PAGE_DATA_SELECTOR);
        return script ? recordProductData(JSON.parse(script.textContent)) : null;
      })
      .catch(() => null);
  }

  function fetchProductData(handle) {
    if (!productLookups.has(handle)) productLookups.set(handle, fetchSectionData(handle));
    return productLookups.get(handle);
  }

  function fetchBatch(handles) {
    const query = encodeURIComponent(handles.join(' '));
    const request = originalFetch(`${rootUrl()}search?view=${BATCH_VIEW}&type=product&q=${query}`, {
      credentials: 'same-origin',
    })
      .then((response) => (response.ok ? response.json() : []))
      .catch(() => [])
      .then((entries) => {
        const found = new Map();
        (Array.isArray(entries) ? entries : []).forEach((entry) => {
          if (entry && entry.handle) found.set(entry.handle, recordProductData(entry.data));
        });
        return found;
      });

    pendingBatches.add(request);
    request.then(() => pendingBatches.delete(request));

    // A handle the batch could not answer falls back to the single-product data section.
    handles.forEach((handle) => {
      productLookups.set(
        handle,
        request.then((found) => (found.get(handle) ? found.get(handle) : fetchSectionData(handle)))
      );
    });
  }

  function flushQueue() {
    flushTimer = null;
    const handles = Array.from(queuedHandles);
    queuedHandles.clear();
    for (let i = 0; i < handles.length; i += BATCH_SIZE) fetchBatch(handles.slice(i, i + BATCH_SIZE));
  }

  function queueHandle(handle) {
    if (!handle || productLookups.has(handle) || queuedHandles.has(handle)) return;
    queuedHandles.add(handle);
    if (!flushTimer) flushTimer = setTimeout(flushQueue, 50);
  }

  function handleFromCard(card) {
    const link = card.querySelector('a[href*="/products/"]');
    const match = link && link.getAttribute('href').match(PRODUCT_PATH_PATTERN);
    return match ? decodeURIComponent(match[1]) : null;
  }

  // Queues every Fast Simon card not seen yet, so new cards from infinite scroll or a filter
  // change are batched without repeating the ones already looked up.
  function scanFastSimonCards() {
    document.querySelectorAll(FAST_SIMON_CARD_SELECTOR).forEach((card) => {
      if (seenElements.has(card)) return;
      const handle = handleFromCard(card);
      if (!handle) return;
      seenElements.add(card);
      queueHandle(handle);
    });
  }

  // /variants/{id} redirects to /products/{handle}?variant={id}; only the final URL is needed.
  function fetchHandleForVariant(variantId) {
    const controller = window.AbortController ? new AbortController() : null;
    return originalFetch(`${rootUrl()}variants/${variantId}`, {
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined,
    })
      .then((response) => {
        if (controller) controller.abort();
        const match = response.url.match(PRODUCT_PATH_PATTERN);
        return match ? decodeURIComponent(match[1]) : null;
      })
      .catch(() => null);
  }

  async function resolveVariant(key) {
    seedFromPage();
    // A tap that lands while its card's batch is still in flight waits for it, not a new lookup.
    if (!variantLookups.has(key) && pendingBatches.size) {
      await Promise.all(Array.from(pendingBatches));
    }
    if (!variantLookups.has(key)) {
      const lookup = fetchHandleForVariant(key)
        .then((handle) => (handle ? fetchProductData(handle) : null))
        .then(() => {
          const resolved = variantLookups.get(key);
          return resolved && resolved !== lookup ? resolved : null;
        })
        .catch(() => null);
      variantLookups.set(key, lookup);
    }
    return variantLookups.get(key);
  }

  function lookupVariant(variantId) {
    return withTimeout(resolveVariant(String(variantId)).catch(() => null));
  }

  function hasProperty(properties, name) {
    return Boolean(properties) && Object.prototype.hasOwnProperty.call(properties, name);
  }

  function isCartAdd(url) {
    try {
      return CART_ADD_PATTERN.test(new URL(String(url), window.location.href).pathname);
    } catch (error) {
      return false;
    }
  }

  // Returns a new body with the property added, or null when nothing needs to change.
  async function enrichBody(body) {
    // Theme forms ({% form 'product' %}) send form_type and already carry the property server-side.
    if (body instanceof FormData) {
      const variantId = body.get('id');
      if (!variantId || body.has('form_type')) return null;
      const property = await lookupVariant(variantId);
      if (!property || body.has(`properties[${property.name}]`)) return null;
      body.append(`properties[${property.name}]`, property.value);
      return body;
    }

    if (body instanceof URLSearchParams || (typeof body === 'string' && !body.trim().startsWith('{'))) {
      const params = new URLSearchParams(body);
      const variantId = params.get('id');
      if (!variantId || params.has('form_type')) return null;
      const property = await lookupVariant(variantId);
      if (!property || params.has(`properties[${property.name}]`)) return null;
      params.append(`properties[${property.name}]`, property.value);
      return body instanceof URLSearchParams ? params : params.toString();
    }

    if (typeof body === 'string') {
      const payload = JSON.parse(body);
      const items = Array.isArray(payload.items) ? payload.items : [payload];
      let changed = false;
      await Promise.all(
        items.map(async (item) => {
          if (!item || !item.id) return;
          const property = await lookupVariant(item.id);
          if (!property || hasProperty(item.properties, property.name)) return;
          item.properties = Object.assign({}, item.properties, { [property.name]: property.value });
          changed = true;
        })
      );
      return changed ? JSON.stringify(payload) : null;
    }

    return null;
  }

  async function safeEnrich(body) {
    try {
      return await enrichBody(body);
    } catch (error) {
      return null;
    }
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = function (resource, init) {
    const url = resource instanceof Request ? resource.url : resource;
    if (!init || !init.body || !isCartAdd(url)) return originalFetch(resource, init);

    return safeEnrich(init.body).then((body) =>
      originalFetch(resource, body ? Object.assign({}, init, { body }) : init)
    );
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, async) {
    this.preorderExpectedDateCartAdd = isCartAdd(url) && async !== false;
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (!this.preorderExpectedDateCartAdd || !body) return originalSend.call(this, body);

    safeEnrich(body).then((enriched) => originalSend.call(this, enriched || body));
  };

  // Fallback warm-up for product cards the grid batching does not cover.
  function prefetchFromEvent(event) {
    const card = event.target.closest && event.target.closest(`${FAST_SIMON_CARD_SELECTOR}, [data-product-id]`);
    const handle = card && handleFromCard(card);
    if (handle) fetchProductData(handle);
  }

  document.addEventListener('pointerover', prefetchFromEvent, { passive: true });
  document.addEventListener('focusin', prefetchFromEvent);

  // Fast Simon draws its grid after load and redraws it on infinite scroll and filter changes.
  // A timer rather than requestAnimationFrame, which never fires in a tab opened in the background.
  let scanScheduled = false;
  function scheduleScan() {
    if (scanScheduled) return;
    scanScheduled = true;
    setTimeout(() => {
      scanScheduled = false;
      scanFastSimonCards();
    }, 100);
  }

  new MutationObserver(scheduleScan).observe(document.documentElement, { childList: true, subtree: true });
  scheduleScan();

  window.preorderExpectedDate = { lookupVariant };
})();
